import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Select, Button, Alert, Modal, Form,
  Input, Switch, Tooltip, Empty, Statistic, Row, Col
} from 'antd';
import {
  ReloadOutlined, EditOutlined, WarningOutlined, ScanOutlined
} from '@ant-design/icons';
import { scanDevicesApi } from './scanDevicesApi';
import { useAppNotifications } from '../../../../utils/notify';
import PageHeader from '../../../../components/PageHeader';
import useAuth from '../../../../hooks/useAuth';

const { Text, Paragraph } = Typography;

// Stage SCAN-2 — "My Scanners", the tenant master's half of the fleet.
//
// Design rules this page follows deliberately:
//   1. ALIAS FIRST. "SC00001" is a hardware serial, not a name an operator can
//      act on, so every row leads with the alias and shows the serial only as
//      small secondary text (a support call still needs it).
//   2. GROUPED BY DEPARTMENT, because that is how the floor is laid out and
//      how a supervisor thinks — not by device id.
//   3. THE ENTRY-WITHOUT-EXIT WARNING IS LOUD. A department with entry
//      scanners and no exit scanner stalls production silently: pieces
//      register arrival and never advance. Nothing else in the ERP would say
//      why.
//   4. Read is open to every role; only the MASTER can change anything.

const ROLE_OPTIONS = [
  { value: 'STAGE_OUT', label: 'Exit — advances the piece' },
  { value: 'STAGE_IN',  label: 'Entry — records arrival only' }
];

const HEALTH = {
  PAIRED:            { color: 'green',   text: 'entry + exit' },
  EXIT_ONLY:         { color: 'blue',    text: 'exit only' },
  ENTRY_ONLY:        { color: 'error',   text: 'ENTRY ONLY — pieces will not advance' },
  NO_ACTIVE_SCANNER: { color: 'warning', text: 'no active scanner' }
};

function sinceLabel(iso) {
  if (!iso) return 'never';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
}
// Heartbeat is a 5-minute poll, so ~11 minutes is two missed beats.
function isOnline(iso) {
  return !!iso && (Date.now() - new Date(iso).getTime()) < 11 * 60 * 1000;
}

export default function ScanDevicesPage() {
  const { me } = useAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const isMaster = me?.isMaster === true;
  const canView = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    return list.includes('*') || list.includes('erp.production.scan_device.view');
  }, [me]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], departments: [], unassignedCount: 0 });
  const [departments, setDepartments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fleet, depts] = await Promise.all([
        scanDevicesApi.list(),
        scanDevicesApi.departments()
      ]);
      setData(fleet || { items: [], departments: [], unassignedCount: 0 });
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (err) {
      notifyError(err, 'Could not load your scanners');
    } finally {
      setLoading(false);
    }
  }, [notifyError]);

  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView, load]);

  const healthByDept = useMemo(
    () => Object.fromEntries((data.departments || []).map((d) => [d.departmentCode, d])),
    [data.departments]
  );

  const brokenDepts = useMemo(
    () => (data.departments || []).filter((d) => d.health === 'ENTRY_ONLY'),
    [data.departments]
  );

  const departmentOptions = useMemo(
    () => departments.map((d) => ({
      value: d.departmentCode,
      label: d.isSubStation ? `   ↳ ${d.departmentName}` : d.departmentName
    })),
    [departments]
  );

  // Rows are grouped by department, unassigned first — those are the ones
  // that need the master's attention.
  const rows = useMemo(() => {
    const items = data.items || [];
    const groups = new Map();
    for (const d of items) {
      const key = d.departmentCode || '__UNASSIGNED__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    }
    const out = [];
    const unassigned = groups.get('__UNASSIGNED__');
    if (unassigned) {
      out.push({ _group: true, key: '__UNASSIGNED__', label: 'Not yet assigned',
        count: unassigned.length, health: null });
      unassigned.forEach((d) => out.push({ ...d, key: d.id }));
      groups.delete('__UNASSIGNED__');
    }
    const ordered = [...groups.keys()].sort((a, b) => {
      const oa = departments.find((x) => x.departmentCode === a)?.executionOrder ?? 999;
      const ob = departments.find((x) => x.departmentCode === b)?.executionOrder ?? 999;
      return oa - ob || a.localeCompare(b);
    });
    for (const code of ordered) {
      const label = departments.find((x) => x.departmentCode === code)?.departmentName || code;
      out.push({ _group: true, key: `g:${code}`, label, count: groups.get(code).length,
        health: healthByDept[code]?.health || null });
      groups.get(code).forEach((d) => out.push({ ...d, key: d.id }));
    }
    return out;
  }, [data.items, departments, healthByDept]);

  function openEdit(row) {
    setEditing(row);
    form.setFieldsValue({
      departmentCode: row.departmentCode || null,
      scanRole: row.scanRole || 'STAGE_OUT',
      displayName: row.displayName || '',
      locationNote: row.locationNote || '',
      isActive: row.isActive !== false
    });
  }

  async function save() {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await scanDevicesApi.patch(editing.id, {
        departmentCode: values.departmentCode || null,
        scanRole: values.scanRole,
        // Blank means "let the server name it from department + role", so the
        // fleet screen never shows a bare serial.
        displayName: values.displayName?.trim() ? values.displayName.trim() : null,
        locationNote: values.locationNote?.trim() || null,
        isActive: values.isActive !== false
      });
      notifySuccess('Scanner updated.');
      setEditing(null);
      load();
    } catch (err) {
      notifyError(err, 'Could not update the scanner');
    } finally {
      setSaving(false);
    }
  }

  if (!canView) {
    return (
      <div>
        <PageHeader title="My Scanners" />
        <Alert type="warning" showIcon message="You do not have access to the scanner fleet."
          description="Ask your master user for the production scanner permission." />
      </div>
    );
  }

  const columns = [
    {
      title: 'Scanner', key: 'name',
      render: (_, r) => {
        if (r._group) {
          const h = r.health ? HEALTH[r.health] : null;
          return {
            children: (
              <Space size={10}>
                <Text strong style={{ textTransform: 'uppercase', letterSpacing: '.06em', fontSize: 12 }}>
                  {r.label}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.count} scanner{r.count === 1 ? '' : 's'}
                </Text>
                {h ? <Tag color={h.color}>{h.text}</Tag> : null}
              </Space>
            ),
            props: { colSpan: 5 }
          };
        }
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{r.displayName || <Text type="secondary">Unnamed</Text>}</Text>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
              {r.deviceId}
            </Text>
          </Space>
        );
      }
    },
    {
      title: 'Position', key: 'role', width: 150,
      render: (_, r) => {
        if (r._group) return { props: { colSpan: 0 } };
        if (!r.departmentCode) return <Text type="secondary">—</Text>;
        return r.scanRole === 'STAGE_IN'
          ? <Tag color="cyan">ENTRY</Tag>
          : <Tag color="blue">EXIT</Tag>;
      }
    },
    {
      title: 'Where', key: 'loc', width: 190,
      render: (_, r) => (r._group ? { props: { colSpan: 0 } }
        : <Text type="secondary" style={{ fontSize: 12 }}>{r.locationNote || '—'}</Text>)
    },
    {
      title: 'Status', key: 'status', width: 170,
      render: (_, r) => {
        if (r._group) return { props: { colSpan: 0 } };
        return (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <span style={{
                display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                background: isOnline(r.lastSeenAt) ? '#52c41a' : 'transparent',
                border: isOnline(r.lastSeenAt) ? 'none' : '1.5px solid #bfbfbf'
              }} />
              <Text type={isOnline(r.lastSeenAt) ? undefined : 'secondary'} style={{ fontSize: 12 }}>
                {sinceLabel(r.lastSeenAt)}
              </Text>
            </Space>
            <Space size={4}>
              {!r.isActive ? <Tag color="orange">Switched off</Tag> : null}
              {r.lockedOut ? (
                <Tooltip title="Locked after repeated bad keys. Contact Glass-i support.">
                  <Tag color="volcano">Locked</Tag>
                </Tooltip>
              ) : null}
            </Space>
          </Space>
        );
      }
    },
    {
      title: '', key: 'actions', width: 100, align: 'right',
      render: (_, r) => {
        if (r._group) return { props: { colSpan: 0 } };
        if (!isMaster) return null;
        return (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            {r.departmentCode ? 'Edit' : 'Assign'}
          </Button>
        );
      }
    }
  ];

  return (
    <div>
      <PageHeader
        title="My Scanners"
        subtitle="Assign each barcode scanner to a department and choose entry or exit"
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />}
      />

      {brokenDepts.length > 0 ? (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message={`${brokenDepts.length} department${brokenDepts.length === 1 ? '' : 's'} will not advance pieces`}
          description={
            <span>
              {brokenDepts.map((d) => <Tag key={d.departmentCode} color="red">{d.departmentCode}</Tag>)}
              {' '}have an <b>entry</b> scanner but no <b>exit</b> scanner. Pieces will register as
              arrived and then never move on. Add an exit scanner, or change one of these to
              &ldquo;Exit&rdquo;.
            </span>
          }
        />
      ) : null}

      {data.unassignedCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${data.unassignedCount} scanner${data.unassignedCount === 1 ? '' : 's'} not yet assigned`}
          description={isMaster
            ? 'These will refuse every scan until you assign them to a department.'
            : 'Your master user needs to assign these to a department before they will work.'}
        />
      ) : null}

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={8}><Card size="small"><Statistic title="Scanners" value={data.items.length} prefix={<ScanOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Online"
          value={data.items.filter((d) => isOnline(d.lastSeenAt)).length}
          valueStyle={{ color: '#389e0d' }} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Departments covered"
          value={(data.departments || []).filter((d) => d.exit > 0).length} /></Card></Col>
      </Row>

      <Card size="small">
        <Table
          rowKey="key"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowClassName={(r) => (r._group ? 'erp-grid__group-row' : '')}
          onRow={(r) => (r._group
            ? { style: { background: 'var(--erp-group-bg, #fafafa)' } }
            : {})}
          locale={{
            emptyText: (
              <Empty description={
                'No scanners yet. Glass-i allocates scanner hardware to your account — '
                + 'contact support if you are expecting some.'
              } />
            )
          }}
        />
      </Card>

      <Modal
        title={editing ? `${editing.displayName || editing.deviceId}` : ''}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        {editing ? (
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
            Hardware {editing.deviceId}
            {editing.hardwareRev ? ` · ${editing.hardwareRev}` : ''}
            {editing.firmwareVersion ? ` · firmware ${editing.firmwareVersion}` : ''}
          </Paragraph>
        ) : null}

        <Form form={form} layout="vertical">
          <Form.Item
            name="departmentCode"
            label="Department"
            extra="Sub-stations (Manual / Machine) are indented. Pick the one this scanner physically stands at."
          >
            <Select
              allowClear
              showSearch
              placeholder="Not assigned"
              options={departmentOptions}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="scanRole"
            label="Position at that department"
            extra="Exit scanners advance the piece to its next stage. Entry scanners only record that it arrived — pair them with an exit scanner to measure how long work sits at a station."
          >
            <Select options={ROLE_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="Name"
            extra="Leave blank and we will name it from the department and position."
          >
            <Input maxLength={60} placeholder="e.g. Furnace Out" />
          </Form.Item>

          <Form.Item name="locationNote" label="Where it is (optional)">
            <Input placeholder="e.g. Tempering line 2, east bay" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
            extra="Switching this off stops the scanner immediately, without touching the hardware."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
